/**
 * PhoneMail — mail client UI inside the phone overlay.
 * Phases: app menu → inbox → message detail → close.
 * Multi-mail triage: inbox lists all mails; reactions play while the phone stays open.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import type {
  PhoneMailAttachment,
  PhoneMailHighlightField,
  PhoneMailHighlightItem,
  PhoneMailInboxItem,
  PhoneMailTriageAction,
  PhoneMailTriageMail,
} from '../types';
import { triageActionHasReactions } from '../systems/phone-mail-triage';
import { getVisibleTextFromInlineTerms } from '../utils/inline-terms';
import { FormattedEmailContent } from './formatted-email-content';
import styles from './phone-mail.module.css';
import { isTypingInEditableField } from '../systems/keyboard-utils';

export type MailPhase = 'menu' | 'inbox' | 'reading';

export interface PhoneMailTriageContext {
  mailId: string;
  allTriaged: boolean;
}

export interface PhoneMailProps {
  from?: string;
  subject?: string;
  content?: string;
  preview?: string;
  receivedAt?: string;
  attachments?: PhoneMailAttachment[];
  attachmentsNote?: string;
  unreadCount?: number;
  continueLabel?: string;
  startInReading?: boolean;
  mailProgress?: string;
  inboxItems?: PhoneMailInboxItem[];
  triageActions?: PhoneMailTriageAction[];
  mails?: PhoneMailTriageMail[];
  reactionsActive?: boolean;
  highlightInEmail?: PhoneMailHighlightItem[];
  inboxReturnSignal?: number;
  /** Inbox-only display; no navigation inside the mail app (used during story choices). */
  previewOnly?: boolean;
  controlledPhase?: MailPhase;
  onPhaseChange?: (phase: MailPhase) => void;
  /** When true, tapping the director mail row calls onMailOpen instead of opening inline. */
  allowOpenMail?: boolean;
  onMailOpen?: () => void;
  /** Inbox visible but rows are not tappable (e.g. during a story choice). */
  inboxLocked?: boolean;
  onComplete?: () => void;
  /** Return false to cancel triage (mail stays untriaged). */
  onTriageAction?: (
    action: PhoneMailTriageAction,
    context?: PhoneMailTriageContext
  ) => boolean | void;
  /** Block triage buttons until the scene entrance animation completes. */
  triageDisabled?: boolean;
  /** Portal target for email zoom overlays (defaults to document.body). */
  overlayPortalTarget?: HTMLElement | null;
  /** Called once when a highlight zoom-in animation reaches the center. */
  onHighlightZoomComplete?: () => void;
  /** Changes when the triage step changes; scopes zoom-complete callbacks. */
  highlightZoomSessionKey?: string;
}

function senderInitial(from: string): string {
  const name = from.replace(/<[^>]+>/, '').trim();
  return (name.charAt(0) || '?').toUpperCase();
}

function senderShort(from: string): string {
  const name = from.replace(/<[^>]+>/, '').trim();
  return name.length > 28 ? `${name.slice(0, 26)}…` : name;
}

function previewFromContent(content: string, preview?: string): string {
  if (preview) return preview;
  const visible = getVisibleTextFromInlineTerms(content);
  const flat = visible.replace(/\n+/g, ' ').trim();
  return flat.slice(0, 72) + (flat.length > 72 ? '…' : '');
}

function emailFieldVisibleText(
  text: string,
  field: PhoneMailHighlightField
): string {
  return field === 'content' ? getVisibleTextFromInlineTerms(text) : text;
}

function getTriageButtonClass(label: string): string {
  const normalized = label.toLowerCase();
  if (
    normalized.includes('archiver')
    || normalized.includes('légitime')
    || normalized.includes('legitime')
  ) {
    return styles.triageBtnArchive ?? '';
  }
  if (normalized.includes('signaler') || normalized.includes('suspect')) {
    return styles.triageBtnReport ?? '';
  }
  if (normalized.includes('cliquer')) {
    return styles.triageBtnDanger ?? '';
  }
  return styles.triageBtnArchive ?? '';
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeRegex(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export const EMAIL_ZOOM_FLY_MS = 600;

type EmailZoomFlyPhase = 'from' | 'to';

type EmailZoomSnapshot = {
  highlights: PhoneMailHighlightItem[];
  field: PhoneMailHighlightField;
  text: string;
  terms: string[];
};

function getEmailZoomCenterRect(
  _from: DOMRect,
  contentWidth: number,
  contentHeight: number
): DOMRect {
  const width = Math.min(contentWidth, window.innerWidth * 0.92);
  const height = contentHeight;
  const left = (window.innerWidth - width) / 2;
  const top = (window.innerHeight - height) / 2 - 80;
  return new DOMRect(left, top, width, height);
}

function rectToFlyStyle(rect: DOMRect): CSSProperties {
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
}

function termsForField(highlights: PhoneMailHighlightItem[], field: PhoneMailHighlightField): string[] {
  return highlights.filter((h) => h.field === field && h.term).map((h) => h.term);
}

function applyEmailZoomMarkers(text: string, terms: string[], active: boolean): string {
  if (!active || !terms.length) return escapeHtml(text);
  let out = escapeHtml(text);
  terms.forEach((term) => {
    const re = new RegExp(escapeRegex(term), 'gi');
    out = out.replace(
      re,
      (match) => `<span class="${styles.emailZoomSource}" data-email-zoom="1">${escapeHtml(match)}</span>`
    );
  });
  return out;
}

function emailZoomFieldLabel(field: PhoneMailHighlightField): string {
  return field === 'from' ? 'De' : 'Message';
}

export const PhoneMail = ({
  from = '',
  subject = '',
  content = '',
  preview,
  receivedAt = '06:47',
  attachments = [],
  attachmentsNote,
  unreadCount = 1,
  continueLabel = 'Fermer',
  startInReading = false,
  mailProgress,
  inboxItems = [],
  triageActions,
  mails,
  reactionsActive = false,
  highlightInEmail = [],
  inboxReturnSignal = 0,
  previewOnly = false,
  controlledPhase,
  onPhaseChange,
  allowOpenMail = false,
  onMailOpen,
  inboxLocked = false,
  onComplete,
  onTriageAction,
  triageDisabled = false,
  overlayPortalTarget = null,
  onHighlightZoomComplete,
  highlightZoomSessionKey = '',
}: PhoneMailProps) => {
  const overlayPortalRoot = overlayPortalTarget ?? document.body;
  const overlayPortalsContained = overlayPortalTarget != null;
  const isMultiMail = Boolean(mails?.length);
  const [internalPhase, setInternalPhase] = useState<MailPhase>(() => {
    if (controlledPhase) return controlledPhase;
    if (previewOnly || isMultiMail) return 'inbox';
    if (startInReading) return 'reading';
    if (mailProgress) return 'inbox';
    return 'menu';
  });
  const phase = controlledPhase ?? internalPhase;

  useEffect(() => {
    if (controlledPhase !== undefined) {
      setInternalPhase(controlledPhase);
    }
  }, [controlledPhase]);

  const goToPhase = useCallback((next: MailPhase) => {
    if (controlledPhase === undefined) {
      setInternalPhase(next);
    }
    onPhaseChange?.(next);
  }, [controlledPhase, onPhaseChange]);
  const [triagedMailIds, setTriagedMailIds] = useState<Set<string>>(() => new Set());
  const [selectedMailId, setSelectedMailId] = useState<string | null>(null);
  const [activeEmailHint, setActiveEmailHint] = useState<string | null>(null);
  const [emailZoomFlyPhase, setEmailZoomFlyPhase] = useState<EmailZoomFlyPhase | null>(null);
  const [emailZoomFromRect, setEmailZoomFromRect] = useState<DOMRect | null>(null);
  const [emailZoomOutgoing, setEmailZoomOutgoing] = useState(false);
  const emailZoomTargetRectRef = useRef<DOMRect | null>(null);
  const emailZoomSnapshotRef = useRef<EmailZoomSnapshot | null>(null);
  const emailZoomDezoomTimerRef = useRef<number | null>(null);
  const emailZoomCompleteTimerRef = useRef<number | null>(null);
  const zoomAnimatingSessionKeyRef = useRef('');
  const prevHighlightKeyRef = useRef('');
  const zoomCompleteNotifiedKeyRef = useRef('');
  const emailZoomMeasureRef = useRef<HTMLDivElement>(null);
  const readingScrollRef = useRef<HTMLDivElement>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const selectedMail = isMultiMail
    ? mails?.find((m) => m.id === selectedMailId) ?? null
    : null;

  const readingFrom = selectedMail?.from ?? from;
  const readingSubject = selectedMail?.subject ?? subject;
  const readingContent = selectedMail?.content ?? content;
  const readingReceivedAt = selectedMail?.receivedAt ?? receivedAt;
  const readingTriageActions = selectedMail?.triageActions ?? triageActions;
  const previewText = previewFromContent(readingContent, selectedMail?.preview ?? preview);

  const remainingUnread = isMultiMail
    ? (mails?.filter((m) => !triagedMailIds.has(m.id)).length ?? 0)
    : unreadCount;

  const advance = useCallback(() => {
    if (phase === 'menu') {
      goToPhase('inbox');
      return;
    }
    if (phase === 'inbox' && !allowOpenMail) {
      goToPhase('reading');
    }
  }, [phase, goToPhase, allowOpenMail]);

  const openMail = useCallback((mailId: string) => {
    if (reactionsActive || triagedMailIds.has(mailId)) return;
    setSelectedMailId(mailId);
    goToPhase('reading');
  }, [reactionsActive, triagedMailIds, goToPhase]);

  const openDirectorMail = useCallback(() => {
    if (!allowOpenMail || !onMailOpen) return;
    onMailOpen();
  }, [allowOpenMail, onMailOpen]);

  const handleClose = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onCompleteRef.current?.();
    },
    []
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Enter') {
        if (isTypingInEditableField()) return;
        e.preventDefault();
        if (phase !== 'reading') advance();
      }
    };
    if (!isMultiMail && phase !== 'reading') {
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
    }
    return undefined;
  }, [phase, advance, isMultiMail]);

  useEffect(() => {
    if (!inboxReturnSignal) return;
    setSelectedMailId(null);
    goToPhase('inbox');
  }, [inboxReturnSignal, goToPhase]);

  useEffect(() => {
    setActiveEmailHint(null);
  }, [readingContent, selectedMailId, phase, reactionsActive]);

  const handleTriage = useCallback(
    (action: PhoneMailTriageAction) => {
      if (isMultiMail && selectedMail) {
        const mailId = selectedMail.id;
        const nextTriaged = new Set(triagedMailIds);
        nextTriaged.add(mailId);
        const handled = onTriageAction?.(action, {
          mailId,
          allTriaged: nextTriaged.size === (mails?.length ?? 0),
        });
        if (handled === false) return;

        setTriagedMailIds(nextTriaged);
        const hasReactions = triageActionHasReactions(action);
        if (!hasReactions) {
          setSelectedMailId(null);
          goToPhase('inbox');
        }
        return;
      }
      onTriageAction?.(action);
    },
    [isMultiMail, selectedMail, triagedMailIds, mails?.length, onTriageAction, goToPhase]
  );

  const emailZoomActive = phase === 'reading' && reactionsActive && highlightInEmail.length > 0;
  const highlightKey = JSON.stringify(highlightInEmail);
  const activeHighlights = emailZoomOutgoing && emailZoomSnapshotRef.current
    ? emailZoomSnapshotRef.current.highlights
    : highlightInEmail;
  const highlightMarkersActive = (emailZoomActive || emailZoomOutgoing) && activeHighlights.length > 0;
  const emailZoomCentered = emailZoomFlyPhase === 'to';
  const emailZoomOverlayActive = emailZoomCentered || emailZoomOutgoing;

  const clearEmailZoomDezoomTimer = useCallback(() => {
    if (emailZoomDezoomTimerRef.current !== null) {
      window.clearTimeout(emailZoomDezoomTimerRef.current);
      emailZoomDezoomTimerRef.current = null;
    }
  }, []);

  const clearEmailZoomCompleteTimer = useCallback(() => {
    if (emailZoomCompleteTimerRef.current !== null) {
      window.clearTimeout(emailZoomCompleteTimerRef.current);
      emailZoomCompleteTimerRef.current = null;
    }
  }, []);

  const completeDezoom = useCallback(() => {
    clearEmailZoomDezoomTimer();
    setEmailZoomOutgoing(false);
    setEmailZoomFlyPhase(null);
    setEmailZoomFromRect(null);
    emailZoomTargetRectRef.current = null;
    emailZoomSnapshotRef.current = null;
  }, [clearEmailZoomDezoomTimer]);

  const beginZoomIn = useCallback(() => {
    const measured = emailZoomMeasureRef.current?.getBoundingClientRect();
    if (!measured?.width) return false;

    const field = highlightInEmail[0]?.field ?? 'from';
    const fieldEl = readingScrollRef.current?.querySelector(
      `[data-email-zoom-field="${field}"]`
    );
    if (!fieldEl) return false;
    const el = fieldEl.querySelector('[data-email-zoom]') ?? fieldEl;

    const fromRect = el.getBoundingClientRect();
    const rawText = field === 'from' ? readingFrom : readingContent;
    const text = emailFieldVisibleText(rawText, field);
    const terms = termsForField(highlightInEmail, field);

    emailZoomTargetRectRef.current = getEmailZoomCenterRect(fromRect, measured.width, measured.height);
    emailZoomSnapshotRef.current = { highlights: highlightInEmail, field, text, terms };
    if (highlightZoomSessionKey) {
      zoomAnimatingSessionKeyRef.current = highlightZoomSessionKey;
    }
    setEmailZoomFromRect(fromRect);
    setEmailZoomFlyPhase('from');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setEmailZoomFlyPhase('to');
      });
    });
    return true;
  }, [highlightInEmail, highlightZoomSessionKey, readingFrom, readingContent]);

  const startDezoom = useCallback(() => {
    if (emailZoomFlyPhase !== 'to' || !emailZoomFromRect || emailZoomOutgoing) return;
    setEmailZoomOutgoing(true);
    requestAnimationFrame(() => {
      setEmailZoomFlyPhase('from');
    });
    clearEmailZoomDezoomTimer();
    emailZoomDezoomTimerRef.current = window.setTimeout(() => {
      emailZoomDezoomTimerRef.current = null;
      const shouldZoomAgain = phase === 'reading' && reactionsActive && highlightInEmail.length > 0;
      completeDezoom();
      if (shouldZoomAgain) {
        requestAnimationFrame(() => {
          if (!beginZoomIn()) {
            requestAnimationFrame(() => beginZoomIn());
          }
        });
      }
    }, EMAIL_ZOOM_FLY_MS);
  }, [
    emailZoomFlyPhase,
    emailZoomFromRect,
    emailZoomOutgoing,
    phase,
    reactionsActive,
    highlightInEmail.length,
    clearEmailZoomDezoomTimer,
    completeDezoom,
    beginZoomIn,
  ]);

  useEffect(() => () => {
    clearEmailZoomDezoomTimer();
    clearEmailZoomCompleteTimer();
  }, [clearEmailZoomDezoomTimer, clearEmailZoomCompleteTimer]);

  useEffect(() => {
    if (!emailZoomActive || emailZoomOutgoing || emailZoomFlyPhase !== 'to' || !highlightZoomSessionKey) {
      if (!emailZoomActive && !emailZoomOutgoing) {
        zoomCompleteNotifiedKeyRef.current = '';
        zoomAnimatingSessionKeyRef.current = '';
      }
      clearEmailZoomCompleteTimer();
      return;
    }
    if (zoomCompleteNotifiedKeyRef.current === highlightZoomSessionKey) return;

    const delayMs = zoomAnimatingSessionKeyRef.current === highlightZoomSessionKey
      ? EMAIL_ZOOM_FLY_MS
      : 0;

    clearEmailZoomCompleteTimer();
    emailZoomCompleteTimerRef.current = window.setTimeout(() => {
      emailZoomCompleteTimerRef.current = null;
      if (zoomCompleteNotifiedKeyRef.current === highlightZoomSessionKey) return;
      zoomCompleteNotifiedKeyRef.current = highlightZoomSessionKey;
      if (zoomAnimatingSessionKeyRef.current === highlightZoomSessionKey) {
        zoomAnimatingSessionKeyRef.current = '';
      }
      onHighlightZoomComplete?.();
    }, delayMs);

    return () => clearEmailZoomCompleteTimer();
  }, [
    emailZoomActive,
    emailZoomOutgoing,
    emailZoomFlyPhase,
    highlightZoomSessionKey,
    onHighlightZoomComplete,
    clearEmailZoomCompleteTimer,
  ]);

  useEffect(() => {
    if (emailZoomOutgoing || emailZoomFlyPhase !== 'to') {
      prevHighlightKeyRef.current = highlightKey;
      return;
    }
    const prevKey = prevHighlightKeyRef.current;
    if (!emailZoomActive && prevKey) {
      startDezoom();
    } else if (emailZoomActive && prevKey && prevKey !== highlightKey) {
      startDezoom();
    }
    prevHighlightKeyRef.current = highlightKey;
  }, [emailZoomActive, highlightKey, emailZoomFlyPhase, emailZoomOutgoing, startDezoom]);

  useLayoutEffect(() => {
    if (emailZoomOutgoing) return;
    if (!emailZoomActive) {
      if (emailZoomFlyPhase === 'to') return;
      if (emailZoomFlyPhase !== null) {
        completeDezoom();
      }
      return;
    }
    if (emailZoomFlyPhase === 'to' || emailZoomFlyPhase === 'from') return;
    if (!beginZoomIn()) {
      requestAnimationFrame(() => beginZoomIn());
    }
  }, [
    emailZoomActive,
    emailZoomOutgoing,
    emailZoomFlyPhase,
    highlightKey,
    beginZoomIn,
    completeDezoom,
  ]);

  const zoomSnapshot = emailZoomSnapshotRef.current;
  const emailZoomField = zoomSnapshot?.field ?? highlightInEmail[0]?.field ?? 'from';
  const emailZoomRawText = zoomSnapshot?.text
    ?? (emailZoomField === 'from' ? readingFrom : readingContent);
  const emailZoomText = emailFieldVisibleText(emailZoomRawText, emailZoomField);
  const emailZoomTerms = zoomSnapshot?.terms ?? termsForField(highlightInEmail, emailZoomField);

  const emailZoomFlyStyle: CSSProperties | undefined = emailZoomFromRect && emailZoomFlyPhase
    ? {
        ...rectToFlyStyle(
          emailZoomFlyPhase === 'to'
            ? (emailZoomTargetRectRef.current ?? emailZoomFromRect)
            : emailZoomFromRect
        ),
        transition: `top ${EMAIL_ZOOM_FLY_MS}ms cubic-bezier(0.4, 0, 0.2, 1), left ${EMAIL_ZOOM_FLY_MS}ms cubic-bezier(0.4, 0, 0.2, 1), width ${EMAIL_ZOOM_FLY_MS}ms cubic-bezier(0.4, 0, 0.2, 1), height ${EMAIL_ZOOM_FLY_MS}ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow ${EMAIL_ZOOM_FLY_MS}ms ease`,
      }
    : undefined;

  const emailZoomCardContent = (
    <>
      <span className={styles.emailZoomCardLabel}>{emailZoomFieldLabel(emailZoomField)}</span>
      <span
        className={[
          styles.emailZoomCardText,
          emailZoomField === 'content' ? styles.emailZoomCardTextContent : '',
        ].filter(Boolean).join(' ')}
        dangerouslySetInnerHTML={{
          __html: applyEmailZoomMarkers(emailZoomText, emailZoomTerms, true),
        }}
      />
    </>
  );

  const emailZoomMeasurePortal = emailZoomActive && !emailZoomOutgoing
    ? createPortal(
        <div ref={emailZoomMeasureRef} className={styles.emailZoomMeasure} aria-hidden>
          <div className={styles.emailZoomCard}>{emailZoomCardContent}</div>
        </div>,
        overlayPortalRoot
      )
    : null;

  const emailZoomPortalVisible = Boolean(
    emailZoomFromRect && emailZoomFlyPhase && (emailZoomActive || emailZoomOutgoing)
  );

  const emailZoomPortal = emailZoomPortalVisible
    ? createPortal(
        <div className={overlayPortalsContained ? styles.emailZoomPortalsContained : undefined}>
          <div
            className={`${styles.emailZoomBackdrop} ${emailZoomOverlayActive ? styles.emailZoomBackdropVisible : ''}`}
            aria-hidden
          />
          <div
            className={[
              styles.emailZoomFlyLayer,
              emailZoomCentered ? styles.emailZoomFlyLayerCentered : '',
            ].filter(Boolean).join(' ')}
            style={emailZoomFlyStyle}
          >
            <div className={styles.emailZoomCard}>{emailZoomCardContent}</div>
          </div>
        </div>,
        overlayPortalRoot
      )
    : null;

  if (phase === 'menu') {
    return (
      <div className={styles.mailContainer} onClick={advance} role="presentation">
        <div className={styles.menuScreen}>
          <div className={styles.menuGrid}>
            <div className={styles.menuItem}>
              <div className={styles.menuIcon}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                </svg>
              </div>
              <span className={styles.menuLabel}>Appel</span>
            </div>
            <div className={styles.menuItem}>
              <div className={styles.menuIcon}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
                </svg>
              </div>
              <span className={styles.menuLabel}>Messages</span>
            </div>
            <div className={`${styles.menuItem} ${styles.menuItemHighlighted}`}>
              {unreadCount > 0 && (
                <span className={styles.menuBadge} aria-label={`${unreadCount} non lus`}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
              <div className={`${styles.menuIcon} ${styles.menuIconMail}`}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                </svg>
              </div>
              <span className={styles.menuLabel}>Mail</span>
            </div>
            <div className={styles.menuItem}>
              <div className={styles.menuIcon}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
                </svg>
              </div>
              <span className={styles.menuLabel}>Alertes</span>
            </div>
          </div>
          <p className={styles.hint}>Appuyez pour ouvrir Mail</p>
        </div>
      </div>
    );
  }

  if (phase === 'inbox') {
    const rows: PhoneMailInboxItem[] = isMultiMail
      ? (mails ?? []).map((mail) => ({
          from: mail.from,
          subject: mail.subject,
          preview: previewFromContent(mail.content, mail.preview),
          receivedAt: mail.receivedAt,
          unread: !triagedMailIds.has(mail.id),
        }))
      : inboxItems.length > 0
        ? inboxItems
        : [{
            from,
            subject,
            preview: previewText,
            receivedAt,
            unread: true,
          }];

    const triagedCount = isMultiMail ? triagedMailIds.size : 0;
    const totalMails = isMultiMail ? (mails?.length ?? 0) : 0;
    const inboxTitle = isMultiMail
      ? `Boîte de réception — ${triagedCount}/${totalMails} traités`
      : mailProgress
        ? `Boîte de réception — ${mailProgress}`
        : 'Boîte de réception';

    return (
      <div
        className={`${styles.mailContainer} ${previewOnly ? styles.mailContainerPreviewOnly : ''}`}
        role="presentation"
      >
        <div className={styles.inboxScreen}>
          <div className={styles.inboxHeader}>
            <div className={styles.inboxTitle}>{inboxTitle}</div>
            <div className={styles.inboxSubtitle}>direction@culture.gouv.fr</div>
          </div>
          <div className={styles.inboxList}>
            {rows.map((row, index) => {
              const mailId = isMultiMail ? mails![index]?.id : undefined;
              const isTriaged = mailId ? triagedMailIds.has(mailId) : false;
              const isOpenable = inboxLocked
                ? false
                : allowOpenMail
                  ? !isMultiMail
                  : !previewOnly && (isMultiMail
                    ? !isTriaged && !reactionsActive
                    : row.from === from && row.subject === subject);

              const handleRowOpen = () => {
                if (allowOpenMail) {
                  openDirectorMail();
                  return;
                }
                if (isMultiMail && mailId) openMail(mailId);
                else advance();
              };

              return (
                <div
                  key={`${row.subject}-${index}`}
                  className={`${styles.mailRow} ${isOpenable && !isMultiMail ? styles.mailRowHighlighted : ''} ${row.unread ? styles.mailRowUnread : ''} ${isTriaged ? styles.mailRowTriaged : ''}`}
                  onClick={isOpenable ? handleRowOpen : undefined}
                  role={isOpenable ? 'button' : undefined}
                  tabIndex={isOpenable ? 0 : undefined}
                  onKeyDown={isOpenable ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleRowOpen();
                    }
                  } : undefined}
                >
                  {row.unread && <span className={styles.unreadDot} aria-hidden />}
                  {isTriaged && <span className={styles.triagedMark} aria-hidden>✓</span>}
                  <div className={styles.mailAvatar}>{senderInitial(row.from)}</div>
                  <div className={styles.mailRowBody}>
                    <div className={styles.mailRowTop}>
                      <span className={styles.mailFrom}>{senderShort(row.from)}</span>
                      <span className={styles.mailTime}>{row.receivedAt ?? receivedAt}</span>
                    </div>
                    <div className={styles.mailSubject}>{row.subject}</div>
                    <div className={styles.mailPreview}>{row.preview ?? previewText}</div>
                  </div>
                </div>
              );
            })}
          </div>
          {!previewOnly && (
            <p className={styles.inboxHint}>
              {allowOpenMail
                ? 'Appuyez pour ouvrir le message'
                : isMultiMail
                  ? remainingUnread > 0
                    ? 'Ouvrir un message non lu'
                    : 'Tous les messages ont été traités'
                  : 'Ouvrir le message'}
            </p>
          )}
        </div>
      </div>
    );
  }

  const isTriagedReading = Boolean(selectedMail && triagedMailIds.has(selectedMail.id));
  const showTriageActions = Boolean(readingTriageActions?.length && !isTriagedReading);

  return (
    <div className={styles.mailContainer} onClick={(e) => e.stopPropagation()} role="presentation">
      {emailZoomMeasurePortal}
      {emailZoomPortal}
      <div className={`${styles.readingScreen} ${emailZoomOverlayActive ? styles.readingScreenDimmed : ''}`}>
        <div className={styles.readingHeader}>
          <button
            type="button"
            className={styles.backBtn}
            disabled={reactionsActive}
            onClick={() => {
              if (reactionsActive) return;
              setSelectedMailId(null);
              goToPhase('inbox');
            }}
          >
            ‹ Boîte
          </button>
        </div>
        <div className={styles.readingScroll} ref={readingScrollRef}>
          <div className={styles.readingMeta}>
            <h1
              className={styles.readingSubject}
              dangerouslySetInnerHTML={{
                __html: escapeHtml(readingSubject),
              }}
            />
            <div className={styles.readingFromRow}>
              <span className={styles.readingFromLabel}>De</span>
              <span
                data-email-zoom-field="from"
                dangerouslySetInnerHTML={{
                  __html: applyEmailZoomMarkers(
                    readingFrom,
                    termsForField(activeHighlights, 'from'),
                    highlightMarkersActive
                  ),
                }}
              />
            </div>
            <div className={styles.readingFromRow}>
              <span className={styles.readingFromLabel}>Reçu</span>
              <span>Aujourd’hui, {readingReceivedAt}</span>
            </div>
          </div>
          <div className={styles.readingBody} data-email-zoom-field="content">
            <FormattedEmailContent
              text={readingContent}
              zoomTerms={termsForField(activeHighlights, 'content')}
              zoomMarkersActive={highlightMarkersActive}
              onHintChange={setActiveEmailHint}
            />
          </div>
          {(attachments.length > 0 || attachmentsNote) && (
            <div className={styles.attachmentsBlock}>
              <div className={styles.attachmentsTitle}>Pièces jointes</div>
              {attachmentsNote && <p className={styles.attachmentsSummary}>{attachmentsNote}</p>}
              {attachments.map((att) => (
                <div key={att.name} className={styles.attachmentItem}>
                  <div className={styles.attachmentIcon}>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
                    </svg>
                  </div>
                  <div className={styles.attachmentMeta}>
                    <div className={styles.attachmentName}>{att.name}</div>
                    {att.size && <div className={styles.attachmentSize}>{att.size}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {activeEmailHint && (
          <div className={styles.emailHintBar} role="status">
            {activeEmailHint}
          </div>
        )}
        <div className={styles.closeBar}>
          {showTriageActions ? (
            <div className={styles.triageActions}>
              {(readingTriageActions ?? []).map((action) => (
                <button
                  key={action.label}
                  type="button"
                  className={`${styles.triageBtn} ${getTriageButtonClass(action.label)}`}
                  disabled={reactionsActive || triageDisabled}
                  onClick={(e) => {
                    if (reactionsActive || triageDisabled) return;
                    e.stopPropagation();
                    handleTriage(action);
                  }}
                >
                  {action.label}
                </button>
              ))}
            </div>
          ) : !isMultiMail ? (
            <button type="button" className={styles.closeBtn} onClick={handleClose}>
              {continueLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};
